import React, { useContext } from "react";
import { Button, Card, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Link, useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyList from "go-core/components/EmptyList";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import AppFormatResourceStatusLabel from "../../../../../../../../../../../components/FormatStatus";
import { ReactComponent as MoreSVG } from "../../../../../../../../../../../images/svg/documents/more.svg";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { PurchaseCorrectionStatus } from "../../../../../../../../../../../services/Api/Organization/types";
import { PurchaseContext } from "../../../pages/Preview";
import { purchaseCorrectionInclude } from "./Form/utils";

const PurchaseCorrectionsCard = () => {
	const { t } = useTranslation();
	const { purchaseCorrections, purchase, fetchPurchaseCorrections } = useContext(PurchaseContext);
	const organization = useSelector(selectOrganization);
	const history = useHistory();
	const { addSuccessFlash, addFlash } = useFlash();
	const confirmation = useConfirmation();
	const location = useLocation();

	const handleAddCorrection = () => {
		history.push(`/${organization.id}/documents/purchases/${purchase.id}/corrections/new`);
	};

	const isCorrectionFromForm = (correctionId: number) => {
		if (location.pathname.endsWith("corrections/new")) {
			return false;
		}

		const pathnameParts = location.pathname.split("/");
		if (
			pathnameParts[pathnameParts.length - 2] === "corrections" &&
			pathnameParts[pathnameParts.length - 1] === correctionId.toString()
		)
			return true;
		return false;
	};

	const handleRemoveCorrection = async (correctionId: number) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await apiOrganization.removePurchaseCorrection(correctionId);
			await fetchPurchaseCorrections();
			if (purchaseCorrections && purchaseCorrections.length - 1 > 0) {
				const newCorrectionId = purchaseCorrections[purchaseCorrections.length - 2].id;
				history.push(`/${organization.id}/documents/purchases/${purchase.id}/corrections/${newCorrectionId}`);
			} else {
				history.push(`/${organization.id}/documents/purchases/${purchase.id}/corrections/new`);
			}
			addSuccessFlash(t("lib:common.flash.removed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleCloseCorrection = async (correctionId: number) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: isCorrectionFromForm(correctionId)
					? t("confirmation.message.close_purchase_correction")
					: t("confirmation.message.action", { ns: "lib" }),
			});
			await apiOrganization.closePurchaseCorrection(correctionId, {
				include: purchaseCorrectionInclude,
			});
			await fetchPurchaseCorrections();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleCloseCancelCorrection = async (correctionId: number) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await apiOrganization.closeCancelPurchaseCorrection(correctionId, {
				include: purchaseCorrectionInclude,
			});
			await fetchPurchaseCorrections();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<Card className="card-box purchase-corrections-card">
			<Card.Body>
				<div className="w-100 d-flex justify-content-end">
					<Button variant="add" onClick={handleAddCorrection}>
						+ {t("lib:common.action.add")}
					</Button>
				</div>
				{purchaseCorrections!.length > 0 ? (
					<table className="table table-preview">
						<thead>
							<tr>
								<th>{t("lib:common.word.number")}</th>
								<th>{t("lib:common.word.date")}</th>
								<th className="action" />
							</tr>
						</thead>
						<tbody>
							{purchaseCorrections!.map((correction) => (
								<tr key={correction.number}>
									<td>
										<div className="d-flex align-items-center gap-1 flex-wrap">
											<Link
												rel="noreferrer"
												to={`/${organization.id}/documents/purchases/${purchase.id}/corrections/${correction.id}`}
											>
												{correction.number}
											</Link>
											<AppFormatResourceStatusLabel
												status={correction.status}
												customStatuses={[
													{
														status: "CLOSED",
														label: "success",
													},
													{
														status: "OPEN",
														label: "blue",
													},
												]}
											/>
										</div>
									</td>
									<td>{FormatDate(correction.corrected_at)}</td>
									<td className="action">
										<div className="d-flex justify-content-end align-items-center gap-2">
											{correction.status === PurchaseCorrectionStatus.OPEN && (
												<Button onClick={() => handleCloseCorrection(correction.id)}>
													{t("lib:common.action.close")}
												</Button>
											)}
											<Dropdown className="icon">
												<Dropdown.Toggle as={MoreSVG} />
												<Dropdown.Menu>
													<Dropdown.Item
														onClick={() =>
															history.push(
																`/${organization.id}/documents/purchases/${purchase.id}/corrections/${correction.id}`
															)
														}
													>
														{t("lib:common.action.edit")}
													</Dropdown.Item>
													{correction.status !== PurchaseCorrectionStatus.VOID && (
														<Dropdown.Item
															onClick={() => handleRemoveCorrection(correction.id)}
														>
															{t("common.action.void")}
														</Dropdown.Item>
													)}
													{correction.status === PurchaseCorrectionStatus.CLOSED && (
														<Dropdown.Item
															onClick={() => handleCloseCancelCorrection(correction.id)}
														>
															{t("common.action.cancel_close")}
														</Dropdown.Item>
													)}
												</Dropdown.Menu>
											</Dropdown>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<EmptyList />
				)}
			</Card.Body>
		</Card>
	);
};

export default PurchaseCorrectionsCard;
